import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

const BG_URL =
  "https://images.unsplash.com/photo-1549946463-0c1ba6fb541f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzaGlweWFyZCUyMG5pZ2h0fGVufDB8fHx8MTc3OTY3OTE5OXww&ixlib=rb-4.1.0&q=85";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#F6F7F8] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-black/70" aria-hidden />
      <div className="absolute inset-0 sos-grid-bg opacity-30" aria-hidden />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-8 flex flex-col items-center text-center gap-4">
          <Logo className="h-16 w-auto" showWordmark={false} />
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#011836]">
              Southern <span className="text-[#0088CC]">Ocean</span>
            </h1>
            <p className="mt-2 text-xs uppercase tracking-[0.35em] text-[#5B6B7C]">
              Solutions
            </p>
          </div>
        </div>

        {sent ? (
          <div className="sos-surface rounded-lg p-6 space-y-4 text-center" data-testid="forgot-password-sent">
            <h2 className="text-lg font-semibold text-[#011836]">Check your inbox</h2>
            <p className="text-sm text-[#5B6B7C]">
              If an account exists for <span className="text-[#011836]">{email}</span>, we've
              sent a password reset link. It expires in 1 hour.
            </p>
            <p className="text-xs text-[#5B6B7C]">
              Didn't get it? Check your spam folder or try again in a few minutes.
            </p>
            <Link
              to="/login"
              className="inline-block text-sm text-[#0088CC] hover:underline font-medium"
              data-testid="forgot-back-to-login-link"
            >
              &larr; Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="sos-surface rounded-lg p-6 space-y-5"
            data-testid="forgot-password-form"
          >
            <div>
              <h2 className="text-lg font-semibold text-[#011836]">Forgot your password?</h2>
              <p className="mt-1 text-sm text-[#5B6B7C]">
                Enter your email and we'll send you a link to reset it.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#5B6B7C]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-[#DDE5EC] text-[#011836] focus-visible:ring-[#0088CC]"
                data-testid="forgot-email-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-[#EF4444] border border-[#EF4444]/30 bg-[#FDECEC]/40 px-3 py-2 rounded"
                data-testid="forgot-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#0088CC] hover:bg-[#006DA6] text-white font-display tracking-wide"
              data-testid="forgot-submit-btn"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
            <div className="text-center pt-2 border-t border-[#DDE5EC]">
              <Link
                to="/login"
                className="text-xs text-[#0088CC] hover:underline font-medium"
                data-testid="forgot-login-link"
              >
                &larr; Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
